import { subDays } from "date-fns"
import { createSlice, isAnyOf } from "@reduxjs/toolkit"
import type { Reducer } from "@reduxjs/toolkit"

import * as AppStorage from "@/base/async-storage"
import { rejectAfterTimeout } from "@/base/promise-timeout"
import { sendRequest } from "@/base/send-request"
import { HEATMAP_RASTER_ENABLED } from "@/components/heatmap-raster"
import { createAsyncThunk } from "@/redux/create-async-thunk"
import type * as Models from "@/redux/models"
import { getDemoFarmIdOrActiveFarmIdFromState } from "@/redux/selectors"

import type { RootState } from "./root.reducer"

const DAYS_BACK = 7
const RESOLUTION_M = 20

/**
 * Client-side bound on a heatmap fetch. `sendRequest` has no timeout of
 * its own, and a stalled connection (infinite OkHttp timeouts on Android)
 * would otherwise leave the id in `loadingIds` forever — blocking every
 * retry for that field and pinning the layers-panel spinner. Comfortably
 * above the ~29 s API Gateway cutoff, so it only fires when the network
 * itself has hung.
 */
export const HEATMAP_REQUEST_TIMEOUT_MS = 35_000

/**
 * How long a cached grid stays authoritative. Map interaction re-dispatches
 * loads constantly (every region change), so entries older than this are
 * allowed through and re-checked against the server. Cheap by design: the
 * server answers from its rev-keyed raster cache (~1 KB, one summary query)
 * unless the field's contributing runs actually changed. Matches the
 * farm-view cache TTL.
 */
export const HEATMAP_CACHE_STALE_MS = 10 * 60 * 1000

interface StatusMapHeatmapState {
  /** Session cache: fieldId → heatmap grid. Cleared on farm switch. */
  cache: Record<number, Models.IrrigationHeatmapGrid>
  /** fieldId → epoch ms of the last successful fetch (staleness check). */
  fetchedAtMs: Record<number, number>
  /** Field IDs currently being fetched */
  loadingIds: number[]
}

const initialState: StatusMapHeatmapState = {
  cache: {},
  fetchedAtMs: {},
  loadingIds: [],
}

/**
 * Load the last 7 days of heatmap data for a single field.
 * Silently skips if the field was fetched recently or is currently loading.
 */
export const loadStatusMapFieldHeatmapAsync = createAsyncThunk<
  { fieldId: number; grid: Models.IrrigationHeatmapGrid },
  number
>(
  "statusMapHeatmap/loadField",
  async (fieldId, { getState }) => {
    const state = getState()
    const activeFarmId = getDemoFarmIdOrActiveFarmIdFromState(state)
    const maxDateMs = Date.now()
    const minDateMs = subDays(new Date(), DAYS_BACK).getTime()
    const grid = await rejectAfterTimeout(
      sendRequest("LoadFieldIrrigationHeatmap", {
        activeFarmId,
        actionArguments: {
          fieldId,
          // With rasters on, skip the per-cell payload entirely — the server
          // then answers from its raster cache (~1 KB) instead of running
          // the full accumulation (~600 KB of cells). Flipping the kill
          // switch restores the cells request for the polygon fallback.
          includeCells: !HEATMAP_RASTER_ENABLED,
          options: { maxDateMs, minDateMs },
          resolutionM: RESOLUTION_M,
        },
      }),
      HEATMAP_REQUEST_TIMEOUT_MS,
      `LoadFieldIrrigationHeatmap timed out for field ${fieldId}`,
    )
    return { fieldId, grid }
  },
  {
    condition: (fieldId, { getState }) => {
      const state = getState() as RootState
      const { cache, fetchedAtMs, loadingIds } = state.statusMapHeatmap
      if (loadingIds.includes(fieldId)) {
        return false
      }
      if (!(fieldId in cache)) {
        return true
      }
      const fetchedAt = fetchedAtMs[fieldId]
      return (
        fetchedAt === undefined ||
        Date.now() - fetchedAt > HEATMAP_CACHE_STALE_MS
      )
    },
  },
)

const slice = createSlice({
  extraReducers: (builder) =>
    builder
      .addCase(loadStatusMapFieldHeatmapAsync.pending, (state, { meta }) => {
        state.loadingIds.push(meta.arg)
      })
      .addCase(loadStatusMapFieldHeatmapAsync.fulfilled, (state, { payload }) => {
        state.cache[payload.fieldId] = payload.grid
        state.fetchedAtMs[payload.fieldId] = Date.now()
        state.loadingIds = state.loadingIds.filter((id) => id !== payload.fieldId)
      })
      .addCase(loadStatusMapFieldHeatmapAsync.rejected, (state, { meta }) => {
        state.loadingIds = state.loadingIds.filter((id) => id !== meta.arg)
      })
      .addMatcher(
        isAnyOf(AppStorage.setActiveFarmId.fulfilled),
        (state) => {
          state.cache = {}
          state.fetchedAtMs = {}
          state.loadingIds = []
        },
      ),
  initialState,
  name: "statusMapHeatmap",
  reducers: {
    /**
     * Drop cached grids so the next load refetches. Used when an active
     * reel run on a field completes: the run leaves the live swath layer,
     * and the server regenerates the field's raster (its rev changed) —
     * without eviction the completed run's coverage would vanish from the
     * map until the session cache cleared.
     */
    evictStatusMapFieldHeatmaps(state, action: { payload: number[] }) {
      const evicted = new Set(action.payload)
      const keep = ([fieldId]: [string, unknown]) =>
        !evicted.has(Number(fieldId))
      state.cache = Object.fromEntries(
        Object.entries(state.cache).filter(keep),
      )
      state.fetchedAtMs = Object.fromEntries(
        Object.entries(state.fetchedAtMs).filter(keep),
      )
    },
  },
})

export const { evictStatusMapFieldHeatmaps } = slice.actions

const statusMapHeatmap: Reducer<StatusMapHeatmapState> = slice.reducer
export default statusMapHeatmap

export function getStatusMapHeatmapCache(state: RootState) {
  return state.statusMapHeatmap.cache
}

/** Whether any field heatmap request is currently in flight. */
export function getStatusMapHeatmapLoading(state: RootState) {
  return state.statusMapHeatmap.loadingIds.length > 0
}
